import { showToast } from '../../engine/toast';
import { t, type Translations } from '../../engine/i18n/i18n';
import { COUNTRIES } from '../../core/payments/countries';
import { submitKyc, KYC_MAX_FILE_BYTES } from './kyc-api';
import type { AccountView } from './settings-api';

function esc(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

const INPUT =
  'w-full min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none';

const STATUS_TONES: Record<string, string> = {
  NONE: 'text-text-dim',
  PENDING: 'text-gold',
  APPROVED: 'text-success',
  REJECTED: 'text-crimson',
};

function statusCopy(status: string): { tone: string; label: string; blurb: string } {
  const known = status in STATUS_TONES ? status : 'NONE';
  return {
    tone: STATUS_TONES[known],
    label: t(`kyc.status.${known}` as keyof Translations),
    blurb: t(`kyc.status.${known}Blurb` as keyof Translations),
  };
}

/** The server refuses a submission unless the status is NONE or REJECTED. */
function canSubmit(status: string): boolean {
  return status === 'NONE' || status === 'REJECTED';
}

function formHtml(): string {
  return `
    <form data-form="kyc" class="glass-panel px-3 py-3 flex flex-col gap-3" novalidate>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">${t('kyc.firstName')}</span>
          <input name="firstName" type="text" required autocomplete="given-name" class="${INPUT}" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">${t('kyc.surname')}</span>
          <input name="lastName" type="text" required autocomplete="family-name" class="${INPUT}" />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">${t('kyc.dateOfBirth')}</span>
        <input name="dateOfBirth" type="date" required autocomplete="bday" class="${INPUT}" />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">${t('kyc.address')}</span>
        <textarea name="address" rows="3" required maxlength="500" autocomplete="street-address"
          placeholder="${t('kyc.addressPlaceholder')}" class="${INPUT} py-2 min-h-20"></textarea>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">${t('kyc.country')}</span>
        <select name="country" required class="${INPUT}">
          <option value="">${t('kyc.selectCountry')}</option>
          ${COUNTRIES.map((c) => `<option value="${esc(c.code)}">${esc(c.name)}</option>`).join('')}
        </select>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">${t('kyc.document')}</span>
        <input name="document" type="file" required accept="image/*,.pdf"
          class="w-full text-xs text-text-dim file:mr-3 file:min-h-11 file:px-3 file:border-0 file:rounded file:bg-gold file:text-bg file:text-sm file:font-bold" />
        <span class="text-[10px] text-text-dim leading-relaxed">
          ${t('kyc.documentHint')}
        </span>
      </label>

      <p class="hidden text-xs text-crimson" data-kyc-error role="alert"></p>

      <button type="submit"
        class="font-button self-start min-h-11 px-4 bg-gold text-bg text-sm rounded hover:bg-gold-bright disabled:bg-border disabled:text-text-dim touch-manipulation">
        ${t('kyc.submit')}
      </button>
    </form>`;
}

export function kycPaneHtml(account: AccountView | null): string {
  const status = account?.kycStatus ?? 'NONE';
  const copy = statusCopy(status);
  const reason = (account as unknown as { kycRejectReason?: string | null } | null)?.kycRejectReason;

  return `
    <section class="flex flex-col gap-4" data-kyc-pane>
      <div>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('kyc.heading')}</h2>
        <div class="glass-panel px-3 py-3 flex flex-col gap-1">
          <div class="text-sm ${copy.tone}">${esc(copy.label)}</div>
          <div class="text-[10px] text-text-dim leading-relaxed">${esc(copy.blurb)}</div>
          ${reason ? `<div class="mt-1 text-[10px] text-crimson leading-relaxed">${esc(t('kyc.reason', { reason }))}</div>` : ''}
        </div>
      </div>
      ${
        canSubmit(status)
          ? `<div>
               <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">
                 ${status === 'REJECTED' ? t('kyc.submitAgain') : t('kyc.yourDetails')}
               </h2>
               ${formHtml()}
             </div>`
          : ''
      }
    </section>`;
}

/**
 * Wires the KYC form.
 *
 * Returns a teardown so the settings screen can drop the listener when the
 * pane changes, matching how it handles the core payment widgets.
 */
export function mountKycPane(paneEl: HTMLElement, onSubmitted: () => void): () => void {
  const form = paneEl.querySelector('[data-form="kyc"]') as HTMLFormElement | null;
  if (!form) return () => {};

  const errorEl = paneEl.querySelector('[data-kyc-error]') as HTMLElement;
  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  function fail(message: string): void {
    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  function onSubmit(e: Event): void {
    e.preventDefault();
    errorEl.classList.add('hidden');

    const data = new FormData(form!);
    const str = (n: string) => String(data.get(n) ?? '').trim();
    const first = str('firstName');
    const last = str('lastName');
    const dateOfBirth = str('dateOfBirth');
    const address = str('address');
    const country = str('country');
    // Read off the input rather than out of FormData: it is the direct source
    // for a file field, and it keeps the size check below independent of how
    // FormData happens to serialise an empty selection.
    const fileInput = form!.querySelector('[name="document"]') as HTMLInputElement | null;
    const document_ = fileInput?.files?.[0] ?? null;

    if (!first || !last) return fail(t('kyc.errName'));
    if (!dateOfBirth) return fail(t('kyc.errDob'));
    if (!address) return fail(t('kyc.errAddress'));
    if (country.length !== 2) return fail(t('kyc.errCountry'));
    if (!(document_ instanceof File) || document_.size === 0) return fail(t('kyc.errDocument'));
    // Checked here as well as server-side: a 10 MB upload that is going to be
    // refused should not be sent over a phone connection first.
    if (document_.size > KYC_MAX_FILE_BYTES) return fail(t('kyc.errTooLarge'));

    submitBtn.disabled = true;
    void (async () => {
      try {
        await submitKyc({
          // Core stores ONE `kycFullName` column; the two inputs exist because
          // that is how people expect to type a name, and they are joined here.
          fullName: `${first} ${last}`,
          dateOfBirth,
          address,
          country,
          document: document_,
        });
        showToast(t('kyc.submitted'), 'success');
        onSubmitted();
      } catch (err) {
        const anyErr = err as { response?: { data?: { message?: unknown } }; message?: string };
        const msg = anyErr.response?.data?.message;
        fail(Array.isArray(msg) ? String(msg[0]) : typeof msg === 'string' ? msg : (anyErr.message ?? t('kyc.submitFailed')));
        submitBtn.disabled = false;
      }
    })();
  }

  form.addEventListener('submit', onSubmit);
  return () => form.removeEventListener('submit', onSubmit);
}
